import { useState, useRef, useEffect } from 'react'
import { AudioAsset, TrackType } from 'types'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './AudioEditorModal.module.scss'
import { Note2, Voice, Play3, Pause, Microphone } from 'blaze-ui/icons'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button/Button'

interface AudioItemProps {
  audio: AudioAsset
  onSelect: () => void
  playingAudioId: string | null
  onPlayingChange: (audioId: string | null) => void
}

const AudioTypeConfig: Record<TrackType | 'default', { icon: React.ElementType; label: string }> = {
  music: {
    icon: Note2,
    label: 'Music',
  },
  sfx: {
    icon: Voice,
    label: 'SFX',
  },
  brand_kit: {
    icon: Microphone,
    label: 'Audio',
  },
  original: {
    icon: Note2,
    label: 'Original',
  },
  default: {
    icon: Note2,
    label: 'Music',
  },
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const AudioItem = ({ audio, onSelect, playingAudioId, onPlayingChange }: AudioItemProps) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [progress, setProgress] = useState(0)
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    const audioElement = audioRef.current
    if (!audioElement) return

    const handleEnded = () => {
      setIsPlaying(false)
      setProgress(0)
      onPlayingChange(null)
    }
    const handlePause = () => setIsPlaying(false)
    const handlePlaying = () => setIsPlaying(true)
    const handleTimeUpdate = () => {
      const currentProgress = (audioElement.currentTime / audioElement.duration) * 100
      setProgress(currentProgress)
    }

    audioElement.addEventListener('ended', handleEnded)
    audioElement.addEventListener('pause', handlePause)
    audioElement.addEventListener('playing', handlePlaying)
    audioElement.addEventListener('timeupdate', handleTimeUpdate)

    return () => {
      audioElement.removeEventListener('ended', handleEnded)
      audioElement.removeEventListener('pause', handlePause)
      audioElement.removeEventListener('playing', handlePlaying)
      audioElement.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [onPlayingChange])

  // Pause this audio when another audio is playing
  useEffect(() => {
    if (playingAudioId !== audio.id && isPlaying) {
      audioRef.current?.pause()
    }
  }, [playingAudioId, audio.id, isPlaying])

  const handlePlayPause = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const audioElement = audioRef.current
    if (!audioElement) return

    if (isPlaying) {
      audioElement.pause()
      onPlayingChange(null)
    } else {
      onPlayingChange(audio.id)
      await audioElement.play()
    }
  }

  const onAddToVideo = () => {
    if (isPlaying) {
      audioRef.current?.pause()
      onPlayingChange(null)
    }

    onSelect()
  }

  const Icon = AudioTypeConfig[audio.type]?.icon || AudioTypeConfig.default.icon
  const label = AudioTypeConfig[audio.type]?.label || AudioTypeConfig.default.label

  return (
    <div className={Styles.audioItem} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <audio ref={audioRef} src={audio.preview_url} preload="none" data-testid="audio-player" />
      <div className={Styles.audioThumbnail}>
        <Icon color="var(--dark-40)" />
        <Text variant="metadata" color="var(--dark-40)">
          {label}
        </Text>

        {(isHovered || isPlaying) && (
          <>
            <div className={Styles.playButtonContainer}>
              <svg className={Styles.progressRing} width="48" height="48" viewBox="0 0 48 48">
                {isPlaying && (
                  <>
                    <circle
                      className={Styles.progressRingBg}
                      stroke="var(--dark-40)"
                      strokeWidth="2"
                      fill="none"
                      r="22"
                      cx="24"
                      cy="24"
                    />
                    <circle
                      className={Styles.progressRingFill}
                      stroke="white"
                      strokeWidth="2"
                      fill="none"
                      r="22"
                      cx="24"
                      cy="24"
                      strokeDasharray={`${2 * Math.PI * 22}`}
                      strokeDashoffset={`${2 * Math.PI * 22 * (1 - progress / 100)}`}
                      strokeLinecap="round"
                    />
                  </>
                )}
              </svg>
              <button
                className={cx(Styles.playButton, { [Styles.playing]: isPlaying })}
                onClick={handlePlayPause}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause /> : <Play3 />}
              </button>
            </div>
          </>
        )}
      </div>

      {isHovered && (
        <div className={Styles.addButtonContainer}>
          <Button variant="secondary" onPress={onAddToVideo} size="sm">
            Add to video
          </Button>
        </div>
      )}

      <div className={Styles.audioInfo}>
        <Text variant="smallList" lineClamp={2}>
          {audio.title}
        </Text>
        <Text variant="smallList" color="var(--dark-40)">
          {formatDuration(audio.duration)}
        </Text>
      </div>
    </div>
  )
}
